import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, } from 'react-redux'
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import Moment from 'moment'

import { showSnackbar, } from 'actions'
import { BookingStatus, } from 'components'
import { TableEditBar } from 'components';
import { deleteBooking, cancelBooking, } from 'firebaseHelpers/bookings'

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
    minWidth: 700
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end'
  }
}));

const Results = props => {
  const { className, bookings, isLoading, fetchBookings, limit, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const [selectedBookings, setSelectedBookings] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isTableEditBarLoading, setTableEditBarLoading] = useState(false)

  const handleSelectAll = event => {
    const selectedBookings = event.target.checked
      ? bookings.map(user => user.id)
      : [];

    setSelectedBookings(selectedBookings);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedBookings.indexOf(id);
    let newSelectedBookings = [];

    if (selectedIndex === -1) {
      newSelectedBookings = newSelectedBookings.concat(selectedBookings, id);
    } else if (selectedIndex === 0) {
      newSelectedBookings = newSelectedBookings.concat(
        selectedBookings.slice(1)
      );
    } else if (selectedIndex === selectedBookings.length - 1) {
      newSelectedBookings = newSelectedBookings.concat(
        selectedBookings.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedBookings = newSelectedBookings.concat(
        selectedBookings.slice(0, selectedIndex),
        selectedBookings.slice(selectedIndex + 1)
      );
    }

    setSelectedBookings(newSelectedBookings);
  };

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(event.target.value);
  };

  const onDeleteBookings = async () => {
    const filteredBookings = bookings.filter((booking) => selectedBookings.includes(booking.id))
    setTableEditBarLoading(true)
    try {
      await Promise.all(filteredBookings.map(deleteBooking))
      setSelectedBookings([])
      fetchBookings()
      setTableEditBarLoading(false)
      dispatch(showSnackbar({ variant: 'success', message: 'Bookings have been deleted successfully' }))
    } catch (error) {
      dispatch(showSnackbar({ variant: 'error', message: error.message }))
      setTableEditBarLoading(false)
    }
  }

  const onCancelBookings = async () => {
    const filteredBookings = bookings.filter((booking) => selectedBookings.includes(booking.id))
    setTableEditBarLoading(true)
    try {
      await Promise.all(filteredBookings.map(cancelBooking))
      setSelectedBookings([])
      fetchBookings()
      setTableEditBarLoading(false)
      dispatch(showSnackbar({ variant: 'success', message: 'Bookings have been canceled successfully' }))
    } catch (error) {
      dispatch(showSnackbar({ variant: 'error', message: error.message }))
      setTableEditBarLoading(false)
    }
  }

  const editBarActions = [
    {
      onClick: onCancelBookings,
      iconElement: CancelIcon,
      label: 'Cancel',
    },
    {
      onClick: onDeleteBookings,
      iconElement: DeleteIcon,
      label: 'Delete',
    }
  ]

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      {!limit && (
        <Typography
          color="textSecondary"
          gutterBottom
          variant="body2"
        >
          {bookings.length} Records found. Page {page + 1} of{' '}
          {Math.ceil(bookings.length / rowsPerPage) || 1}
        </Typography>
      )}
      <Card>
        <CardHeader title={limit ? `${limit} Last Bookings` : 'All bookings'}/>
        <Divider />
        <CardContent className={classes.content}>
          {isLoading && <LinearProgress />}
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBookings.length === bookings.length}
                        color="primary"
                        indeterminate={
                          selectedBookings.length > 0 &&
                          selectedBookings.length < bookings.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Parking Name</TableCell>
                    <TableCell>Number Plate</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Total Price</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage).map(booking => (
                    <TableRow
                      hover
                      key={booking.id}
                      selected={selectedBookings.indexOf(booking.id) !== -1}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            selectedBookings.indexOf(booking.id) !== -1
                          }
                          color="primary"
                          onChange={event =>
                            handleSelectOne(event, booking.id)
                          }
                          value={selectedBookings.indexOf(booking.id) !== -1}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={classes.nameCell}>
                          <div>
                            <Link
                              color="inherit"
                              component={RouterLink}
                              to={`/management/bookings/${booking.id}`}
                              variant="h6"
                            >
                              {booking.reference}
                              <Typography variant="body2">
                                {Moment(booking.reservationDate).utcOffset(booking.utcOffset).format(
                                  'DD MMM YYYY | hh:mm'
                                )} UTC {Moment(booking.reservationDate).utcOffset(booking.utcOffset).format('Z')}
                              </Typography>
                            </Link>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{booking.parkingName}</TableCell>
                      <TableCell>
                        {booking.numberPlate}
                      </TableCell>
                      <TableCell>{Moment(booking.from).utcOffset(booking.utcOffset).format('lll')} UTC {Moment(booking.from).utcOffset(booking.utcOffset).format('Z')}</TableCell>
                      <TableCell>{Moment(booking.to).utcOffset(booking.utcOffset).format('lll')} UTC {Moment(booking.to).utcOffset(booking.utcOffset).format('Z')}</TableCell>
                      <TableCell><BookingStatus status={booking.status}/></TableCell>
                      <TableCell>{`${booking.totalPrice} ${booking.currency}`}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="primary"
                          component={RouterLink}
                          size="small"
                          to={`/management/bookings/${booking.id}`}
                          variant="outlined"
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
        {!limit && (
          <CardActions className={classes.actions}>
            <TablePagination
              component="div"
              count={bookings.length}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </CardActions>
        )}
      </Card>
      <TableEditBar selected={selectedBookings} actions={editBarActions} isLoading={isTableEditBarLoading}/>
    </div>
  );
};

Results.propTypes = {
  isLoading: PropTypes.bool,
  limit: PropTypes.number,
  className: PropTypes.string,
  bookings: PropTypes.array.isRequired,
  fetchBookings: PropTypes.func,
};

Results.defaultProps = {
  bookings: []
};

export default Results;
